import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { CreditCard, CheckCircle2, Lock, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import api, { downloadUrl, formatApiError } from "../lib/api";

function PayPalArea({ invoice, onPaid }) {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  const [failed, setFailed] = useState(false);
  if (isRejected || failed) {
    return (
      <p className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5" data-testid="paypal-load-error">
        PayPal couldn't load right now — please refresh the page or pay with Razorpay above.
      </p>
    );
  }
  return (
    <>
      {isPending && <div className="h-11 rounded-lg bg-slate-100 animate-pulse" data-testid="paypal-loading" />}
      <PayPalButtons
        style={{ layout: "horizontal", height: 44, tagline: false }}
        createOrder={async () => {
          const { data } = await api.post(`/invoices/${invoice.id}/paypal/order`);
          return data.order_id;
        }}
        onApprove={async (data) => {
          try {
            await api.post(`/invoices/${invoice.id}/paypal/capture`, { order_id: data.orderID });
            toast.success("Payment successful — your plan is unlocked");
            onPaid();
          } catch (e) { toast.error(formatApiError(e)); }
        }}
        onError={(err) => {
          if (String(err?.message || err).toLowerCase().includes("destroyed")) setFailed(true);
          else toast.error("PayPal payment failed — please try again");
        }}
      />
    </>
  );
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(s);
  });
}

export function InvoicePanel({ invoice, onPaid }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, []);

  if (!invoice) return null;
  const paid = invoice.status === "paid";
  const amountLabel = `${invoice.currency} ${Number(invoice.amount).toFixed(2)}`;

  const payRazorpay = async () => {
    setBusy(true);
    try {
      await loadRazorpayScript();
      const { data } = await api.post(`/invoices/${invoice.id}/razorpay/order`);
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "OrthoPlaniX",
        description: `Case ${invoice.case_ref}`,
        theme: { color: "#0284C7" },
        handler: async (res) => {
          try {
            await api.post(`/invoices/${invoice.id}/razorpay/verify`, res);
            toast.success("Payment successful — your plan is unlocked");
            onPaid();
          } catch (e) { toast.error(formatApiError(e)); }
        },
      });
      rzp.open();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-xl p-6 ${paid ? "bg-emerald-50/70 border-emerald-200" : "bg-white border-amber-300"}`} data-testid="invoice-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">Invoice</h2>
        {paid ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-600" data-testid="invoice-paid-badge">
            <CheckCircle2 className="h-3.5 w-3.5" /> Paid{invoice.payment_method ? ` · ${invoice.payment_method}` : ""}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-600" data-testid="invoice-unpaid-badge">
            <Lock className="h-3.5 w-3.5" /> Unpaid
          </span>
        )}
      </div>
      <p className="font-display font-black text-3xl" data-testid="invoice-amount">{amountLabel}</p>
      {paid ? (
        <>
          <p className="text-xs text-slate-500 mt-2">Paid {invoice.paid_at ? new Date(invoice.paid_at).toLocaleString() : ""} — your final plan is unlocked.</p>
          <Button onClick={() => downloadUrl(`/invoices/${invoice.id}/receipt`, `receipt-${invoice.case_ref}.pdf`).catch((e) => toast.error(formatApiError(e)))}
            variant="outline" data-testid="invoice-receipt-btn"
            className="mt-4 w-full rounded-full font-bold border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-10">
            <FileDown className="h-4 w-4 mr-2" /> Download receipt (PDF)
          </Button>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">Your final treatment plan will unlock as soon as this invoice is paid.</p>
          {config === null ? (
            <div className="mt-4 h-9 rounded-lg bg-slate-100 animate-pulse" />
          ) : !config.razorpay_key_id && !config.paypal_client_id ? (
            <p className="mt-4 text-xs font-semibold text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5" data-testid="payments-not-configured">
              Online payment is being enabled — please check back shortly or contact the planning team.
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {config.razorpay_key_id && (
                <Button onClick={payRazorpay} disabled={busy} data-testid="pay-razorpay-btn"
                  className="w-full rounded-full bg-[#0f2c66] hover:bg-[#143a85] font-bold h-11">
                  <CreditCard className="h-4 w-4 mr-2" /> Pay with Razorpay
                </Button>
              )}
              {config.paypal_client_id && invoice.currency !== "INR" && (
                <div data-testid="paypal-buttons-wrap">
                  <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: invoice.currency }}>
                    <PayPalArea invoice={invoice} onPaid={onPaid} />
                  </PayPalScriptProvider>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
